export const createSearchController = ({
  state,
  searchInput,
  searchResults,
  searchEntryId,
  escapeHtml,
  formatPosition,
  resolveMappingObject,
  resolvePlacement,
  handlePlacementFocus,
  handleMappingFocus
}) => {
  const hideSearchResults = () => {
    state.activeSearchResultId = null
    searchResults?.setAttribute("hidden", "hidden")
  }

  const filteredSearchEntries = () => {
    const query = searchInput?.value.toString().trim().toLowerCase()
    if (!query) return []

    return state.searchEntries
      .filter((entry) => entry.searchText.includes(query))
      .slice(0, 8)
  }

  const renderSearchResults = () => {
    if (!searchResults) return

    const query = searchInput?.value.toString().trim()
    if (!query) {
      hideSearchResults()
      return
    }

    const results = filteredSearchEntries()
    if (results.length === 0) {
      searchResults.innerHTML = '<p class="plan-viewer-search-empty">No matching things or objects.</p>'
      searchResults.removeAttribute("hidden")
      return
    }

    if (!results.some((entry) => searchEntryId(entry) === state.activeSearchResultId)) {
      state.activeSearchResultId = searchEntryId(results[0])
    }

    searchResults.innerHTML = results
      .map((entry) => `
        <button
          type="button"
          class="plan-viewer-search-result${searchEntryId(entry) === state.activeSearchResultId ? " is-active" : ""}"
          data-search-entry-id="${searchEntryId(entry)}">
          <span class="plan-viewer-search-result-kicker">${escapeHtml(entry.category)}</span>
          <span class="plan-viewer-search-result-title">${escapeHtml(entry.title)}</span>
          <span class="plan-viewer-search-result-subtitle">${escapeHtml(entry.subtitle)}</span>
        </button>
      `)
      .join("")

    searchResults.removeAttribute("hidden")
  }

  const rebuildSearchEntries = () => {
    const mappingEntries = Array.from(state.mappingsByNodeKey.values()).map((mapping) => {
      const object = resolveMappingObject(mapping.model_node_key)
      return {
        kind: "mapping",
        key: mapping.model_node_key,
        title: mapping.thing.name,
        subtitle: mapping.model_node_name || mapping.model_node_key,
        category: mapping.thing.kind ? `Tagged ${mapping.thing.kind}` : "Tagged node",
        searchText: [
          mapping.thing.name,
          mapping.thing.kind,
          mapping.thing.description,
          mapping.model_node_name,
          mapping.model_node_key,
          object?.name
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
      }
    })

    const placementEntries = Array.from(state.placementsById.values()).map((placement) => ({
      kind: "placement",
      key: placement.id,
      title: placement.thing.name,
      subtitle: formatPosition(placement.render_position || placement.position),
      category: placement.thing.kind ? `Placed ${placement.thing.kind}` : "Placed object",
      searchText: [
        placement.thing.name,
        placement.thing.kind,
        placement.thing.description,
        formatPosition(placement.render_position || placement.position)
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
    }))

    state.searchEntries = [...placementEntries, ...mappingEntries].sort((left, right) => left.title.localeCompare(right.title))
    renderSearchResults()
  }

  const focusSearchEntry = (entry) => {
    if (!entry) return

    if (entry.kind === "placement") {
      const placement = resolvePlacement(entry.key)
      if (!placement) return

      handlePlacementFocus(placement)
      hideSearchResults()
      return
    }

    const object = resolveMappingObject(entry.key)
    const mapping = state.mappingsByNodeKey.get(entry.key)
    if (!object || !mapping) return

    handleMappingFocus(object, mapping)
    hideSearchResults()
  }

  return {
    hideSearchResults,
    filteredSearchEntries,
    renderSearchResults,
    rebuildSearchEntries,
    focusSearchEntry
  }
}
